/**
 * Natural Phonics (自然拼读) Learning System
 * Supports both American and British English pronunciation rules
 */

export interface PhonicsRule {
  id: string;
  pattern: string;           // e.g., "a_e", "igh"
  description: string;       // Chinese description
  examples: PhonicsExample[];
  category: 'short-vowel' | 'long-vowel' | 'consonant' | 'blend' | 'digraph' | 'special';
  americanSound: string;     // American IPA
  britishSound?: string;     // British IPA (if different)
}

export interface PhonicsExample {
  word: string;
  americanPhonetic: string;
  britishPhonetic: string;
  image?: string;
  meaning: string;
}

export interface PhonicsCategory {
  id: string;
  name: string;
  icon: string;
  description: string;
  rules: PhonicsRule[];
}

/**
 * Short Vowels (短元音) - 美式和英式基本相同
 */
const shortVowels: PhonicsCategory = {
  id: 'short-vowels',
  name: '短元音',
  icon: '🔤',
  description: '基础短元音发音规则',
  rules: [
    {
      id: 'short-a',
      pattern: 'a',
      description: '字母 a 在闭音节中发短音 /æ/',
      category: 'short-vowel',
      americanSound: '/æ/',
      britishSound: '/æ/',
      examples: [
        { word: 'cat', americanPhonetic: '/kæt/', britishPhonetic: '/kæt/', meaning: '猫' },
        { word: 'hat', americanPhonetic: '/hæt/', britishPhonetic: '/hæt/', meaning: '帽子' },
        { word: 'map', americanPhonetic: '/mæp/', britishPhonetic: '/mæp/', meaning: '地图' },
        { word: 'bag', americanPhonetic: '/bæɡ/', britishPhonetic: '/bæɡ/', meaning: '包' },
        { word: 'dad', americanPhonetic: '/dæd/', britishPhonetic: '/dæd/', meaning: '爸爸' },
      ]
    },
    {
      id: 'short-e',
      pattern: 'e',
      description: '字母 e 在闭音节中发短音 /e/',
      category: 'short-vowel',
      americanSound: '/e/',
      britishSound: '/e/',
      examples: [
        { word: 'bed', americanPhonetic: '/bed/', britishPhonetic: '/bed/', meaning: '床' },
        { word: 'red', americanPhonetic: '/red/', britishPhonetic: '/red/', meaning: '红色' },
        { word: 'pen', americanPhonetic: '/pen/', britishPhonetic: '/pen/', meaning: '钢笔' },
        { word: 'leg', americanPhonetic: '/leɡ/', britishPhonetic: '/leɡ/', meaning: '腿' },
        { word: 'egg', americanPhonetic: '/eɡ/', britishPhonetic: '/eɡ/', meaning: '鸡蛋' },
      ]
    },
    {
      id: 'short-i',
      pattern: 'i',
      description: '字母 i 在闭音节中发短音 /ɪ/',
      category: 'short-vowel',
      americanSound: '/ɪ/',
      britishSound: '/ɪ/',
      examples: [
        { word: 'sit', americanPhonetic: '/sɪt/', britishPhonetic: '/sɪt/', meaning: '坐' },
        { word: 'pig', americanPhonetic: '/pɪɡ/', britishPhonetic: '/pɪɡ/', meaning: '猪' },
        { word: 'big', americanPhonetic: '/bɪɡ/', britishPhonetic: '/bɪɡ/', meaning: '大' },
        { word: 'lip', americanPhonetic: '/lɪp/', britishPhonetic: '/lɪp/', meaning: '嘴唇' },
        { word: 'win', americanPhonetic: '/wɪn/', britishPhonetic: '/wɪn/', meaning: '赢' },
      ]
    },
    {
      id: 'short-o',
      pattern: 'o',
      description: '字母 o 在闭音节中发短音，美式 /ɑ/，英式 /ɒ/',
      category: 'short-vowel',
      americanSound: '/ɑ/',
      britishSound: '/ɒ/',
      examples: [
        { word: 'hot', americanPhonetic: '/hɑt/', britishPhonetic: '/hɒt/', meaning: '热' },
        { word: 'dog', americanPhonetic: '/dɑɡ/', britishPhonetic: '/dɒɡ/', meaning: '狗' },
        { word: 'box', americanPhonetic: '/bɑks/', britishPhonetic: '/bɒks/', meaning: '盒子' },
        { word: 'fox', americanPhonetic: '/fɑks/', britishPhonetic: '/fɒks/', meaning: '狐狸' },
        { word: 'top', americanPhonetic: '/tɑp/', britishPhonetic: '/tɒp/', meaning: '顶部' },
      ]
    },
    {
      id: 'short-u',
      pattern: 'u',
      description: '字母 u 在闭音节中发短音 /ʌ/',
      category: 'short-vowel',
      americanSound: '/ʌ/',
      britishSound: '/ʌ/',
      examples: [
        { word: 'cup', americanPhonetic: '/kʌp/', britishPhonetic: '/kʌp/', meaning: '杯子' },
        { word: 'bus', americanPhonetic: '/bʌs/', britishPhonetic: '/bʌs/', meaning: '公交车' },
        { word: 'sun', americanPhonetic: '/sʌn/', britishPhonetic: '/sʌn/', meaning: '太阳' },
        { word: 'nut', americanPhonetic: '/nʌt/', britishPhonetic: '/nʌt/', meaning: '坚果' },
        { word: 'cut', americanPhonetic: '/kʌt/', britishPhonetic: '/kʌt/', meaning: '切' },
      ]
    },
  ]
};

/**
 * Long Vowels (长元音) - Magic E 规则
 */
const longVowels: PhonicsCategory = {
  id: 'long-vowels',
  name: '长元音',
  icon: '📏',
  description: 'Magic E 规则：元音+辅音+e，元音发长音',
  rules: [
    {
      id: 'long-a-magic-e',
      pattern: 'a_e',
      description: 'a + 辅音 + e 结构，a 发长音 /eɪ/',
      category: 'long-vowel',
      americanSound: '/eɪ/',
      britishSound: '/eɪ/',
      examples: [
        { word: 'cake', americanPhonetic: '/keɪk/', britishPhonetic: '/keɪk/', meaning: '蛋糕' },
        { word: 'make', americanPhonetic: '/meɪk/', britishPhonetic: '/meɪk/', meaning: '制作' },
        { word: 'name', americanPhonetic: '/neɪm/', britishPhonetic: '/neɪm/', meaning: '名字' },
        { word: 'late', americanPhonetic: '/leɪt/', britishPhonetic: '/leɪt/', meaning: '晚' },
        { word: 'face', americanPhonetic: '/feɪs/', britishPhonetic: '/feɪs/', meaning: '脸' },
      ]
    },
    {
      id: 'long-i-magic-e',
      pattern: 'i_e',
      description: 'i + 辅音 + e 结构，i 发长音 /aɪ/',
      category: 'long-vowel',
      americanSound: '/aɪ/',
      britishSound: '/aɪ/',
      examples: [
        { word: 'like', americanPhonetic: '/laɪk/', britishPhonetic: '/laɪk/', meaning: '喜欢' },
        { word: 'time', americanPhonetic: '/taɪm/', britishPhonetic: '/taɪm/', meaning: '时间' },
        { word: 'bike', americanPhonetic: '/baɪk/', britishPhonetic: '/baɪk/', meaning: '自行车' },
        { word: 'nice', americanPhonetic: '/naɪs/', britishPhonetic: '/naɪs/', meaning: '好' },
        { word: 'kite', americanPhonetic: '/kaɪt/', britishPhonetic: '/kaɪt/', meaning: '风筝' },
      ]
    },
    {
      id: 'long-o-magic-e',
      pattern: 'o_e',
      description: 'o + 辅音 + e 结构，美式 /oʊ/，英式 /əʊ/',
      category: 'long-vowel',
      americanSound: '/oʊ/',
      britishSound: '/əʊ/',
      examples: [
        { word: 'home', americanPhonetic: '/hoʊm/', britishPhonetic: '/həʊm/', meaning: '家' },
        { word: 'bone', americanPhonetic: '/boʊn/', britishPhonetic: '/bəʊn/', meaning: '骨头' },
        { word: 'rose', americanPhonetic: '/roʊz/', britishPhonetic: '/rəʊz/', meaning: '玫瑰' },
        { word: 'nose', americanPhonetic: '/noʊz/', britishPhonetic: '/nəʊz/', meaning: '鼻子' },
        { word: 'hope', americanPhonetic: '/hoʊp/', britishPhonetic: '/həʊp/', meaning: '希望' },
      ]
    },
    {
      id: 'long-u-magic-e',
      pattern: 'u_e',
      description: 'u + 辅音 + e 结构，u 发长音',
      category: 'long-vowel',
      americanSound: '/u/',
      britishSound: '/u/',
      examples: [
        { word: 'tube', americanPhonetic: '/tuːb/', britishPhonetic: '/tuːb/', meaning: '管子' },
        { word: 'cute', americanPhonetic: '/kuːt/', britishPhonetic: '/kuːt/', meaning: '可爱' },
        { word: 'rule', americanPhonetic: '/ruːl/', britishPhonetic: '/ruːl/', meaning: '规则' },
        { word: 'huge', americanPhonetic: '/huːdʒ/', britishPhonetic: '/huːdʒ/', meaning: '巨大' },
      ]
    },
  ]
};

/**
 * Consonant Digraphs (辅音字母组合)
 */
const digraphs: PhonicsCategory = {
  id: 'digraphs',
  name: '辅音组合',
  icon: '🔗',
  description: '两个或多个字母组合发一个音',
  rules: [
    {
      id: 'sh',
      pattern: 'sh',
      description: 'sh 组合发 /ʃ/ 音',
      category: 'digraph',
      americanSound: '/ʃ/',
      britishSound: '/ʃ/',
      examples: [
        { word: 'ship', americanPhonetic: '/ʃɪp/', britishPhonetic: '/ʃɪp/', meaning: '船' },
        { word: 'shop', americanPhonetic: '/ʃɑp/', britishPhonetic: '/ʃɒp/', meaning: '商店' },
        { word: 'fish', americanPhonetic: '/fɪʃ/', britishPhonetic: '/fɪʃ/', meaning: '鱼' },
        { word: 'she', americanPhonetic: '/ʃi/', britishPhonetic: '/ʃi/', meaning: '她' },
        { word: 'shoe', americanPhonetic: '/ʃu/', britishPhonetic: '/ʃu/', meaning: '鞋子' },
      ]
    },
    {
      id: 'ch',
      pattern: 'ch',
      description: 'ch 组合发 /tʃ/ 或 /k/ 音',
      category: 'digraph',
      americanSound: '/tʃ/',
      britishSound: '/tʃ/',
      examples: [
        { word: 'chair', americanPhonetic: '/tʃer/', britishPhonetic: '/tʃeə/', meaning: '椅子' },
        { word: 'child', americanPhonetic: '/tʃaɪld/', britishPhonetic: '/tʃaɪld/', meaning: '孩子' },
        { word: 'chicken', americanPhonetic: '/ˈtʃɪkɪn/', britishPhonetic: '/ˈtʃɪkɪn/', meaning: '鸡' },
        { word: 'school', americanPhonetic: '/skuːl/', britishPhonetic: '/skuːl/', meaning: '学校 (发 /k/)' },
        { word: 'church', americanPhonetic: '/tʃɜrtʃ/', britishPhonetic: '/tʃɜːtʃ/', meaning: '教堂' },
      ]
    },
    {
      id: 'th-voiceless',
      pattern: 'th',
      description: 'th 组合发清音 /θ/ (think) 或浊音 /ð/ (this)',
      category: 'digraph',
      americanSound: '/θ/',
      britishSound: '/θ/',
      examples: [
        { word: 'think', americanPhonetic: '/θɪŋk/', britishPhonetic: '/θɪŋk/', meaning: '思考 (清音)' },
        { word: 'three', americanPhonetic: '/θri/', britishPhonetic: '/θri/', meaning: '三 (清音)' },
        { word: 'this', americanPhonetic: '/ðɪs/', britishPhonetic: '/ðɪs/', meaning: '这个 (浊音)' },
        { word: 'that', americanPhonetic: '/ðæt/', britishPhonetic: '/ðæt/', meaning: '那个 (浊音)' },
        { word: 'mother', americanPhonetic: '/ˈmʌðər/', britishPhonetic: '/ˈmʌðə/', meaning: '妈妈 (浊音)' },
      ]
    },
    {
      id: 'ng',
      pattern: 'ng',
      description: 'ng 组合发 /ŋ/ 音',
      category: 'digraph',
      americanSound: '/ŋ/',
      britishSound: '/ŋ/',
      examples: [
        { word: 'sing', americanPhonetic: '/sɪŋ/', britishPhonetic: '/sɪŋ/', meaning: '唱歌' },
        { word: 'ring', americanPhonetic: '/rɪŋ/', britishPhonetic: '/rɪŋ/', meaning: '戒指' },
        { word: 'king', americanPhonetic: '/kɪŋ/', britishPhonetic: '/kɪŋ/', meaning: '国王' },
        { word: 'long', americanPhonetic: '/lɑŋ/', britishPhonetic: '/lɒŋ/', meaning: '长' },
        { word: 'song', americanPhonetic: '/sɑŋ/', britishPhonetic: '/sɒŋ/', meaning: '歌曲' },
      ]
    },
    {
      id: 'ck',
      pattern: 'ck',
      description: 'ck 组合发 /k/ 音',
      category: 'digraph',
      americanSound: '/k/',
      britishSound: '/k/',
      examples: [
        { word: 'back', americanPhonetic: '/bæk/', britishPhonetic: '/bæk/', meaning: '背' },
        { word: 'duck', americanPhonetic: '/dʌk/', britishPhonetic: '/dʌk/', meaning: '鸭子' },
        { word: 'clock', americanPhonetic: '/klɑk/', britishPhonetic: '/klɒk/', meaning: '钟' },
        { word: 'black', americanPhonetic: '/blæk/', britishPhonetic: '/blæk/', meaning: '黑色' },
        { word: 'kick', americanPhonetic: '/kɪk/', britishPhonetic: '/kɪk/', meaning: '踢' },
      ]
    },
  ]
};

/**
 * Long Vowel Teams (元音组合)
 */
const vowelTeams: PhonicsCategory = {
  id: 'vowel-teams',
  name: '元音组合',
  icon: '👥',
  description: '两个元音在一起的发音规则',
  rules: [
    {
      id: 'ee-team',
      pattern: 'ee',
      description: 'ee 组合发长音 /i/',
      category: 'long-vowel',
      americanSound: '/i/',
      britishSound: '/i/',
      examples: [
        { word: 'see', americanPhonetic: '/si/', britishPhonetic: '/si/', meaning: '看见' },
        { word: 'tree', americanPhonetic: '/tri/', britishPhonetic: '/tri/', meaning: '树' },
        { word: 'bee', americanPhonetic: '/bi/', britishPhonetic: '/bi/', meaning: '蜜蜂' },
        { word: 'green', americanPhonetic: '/ɡrin/', britishPhonetic: '/ɡriːn/', meaning: '绿色' },
        { word: 'sleep', americanPhonetic: '/slip/', britishPhonetic: '/sliːp/', meaning: '睡觉' },
      ]
    },
    {
      id: 'ea-team',
      pattern: 'ea',
      description: 'ea 组合发长音 /eɪ/ 或 /i/',
      category: 'long-vowel',
      americanSound: '/i/',
      britishSound: '/i/',
      examples: [
        { word: 'sea', americanPhonetic: '/si/', britishPhonetic: '/si/', meaning: '海' },
        { word: 'tea', americanPhonetic: '/ti/', britishPhonetic: '/ti/', meaning: '茶' },
        { word: 'eat', americanPhonetic: '/it/', britishPhonetic: '/iːt/', meaning: '吃' },
        { word: 'read', americanPhonetic: '/rid/', britishPhonetic: '/riːd/', meaning: '阅读' },
        { word: 'team', americanPhonetic: '/tim/', britishPhonetic: '/tiːm/', meaning: '团队' },
      ]
    },
    {
      id: 'ai-team',
      pattern: 'ai',
      description: 'ai 组合发 /eɪ/ 音',
      category: 'long-vowel',
      americanSound: '/eɪ/',
      britishSound: '/eɪ/',
      examples: [
        { word: 'rain', americanPhonetic: '/reɪn/', britishPhonetic: '/reɪn/', meaning: '雨' },
        { word: 'train', americanPhonetic: '/treɪn/', britishPhonetic: '/treɪn/', meaning: '火车' },
        { word: 'wait', americanPhonetic: '/weɪt/', britishPhonetic: '/weɪt/', meaning: '等待' },
        { word: 'day', americanPhonetic: '/deɪ/', britishPhonetic: '/deɪ/', meaning: '天' },
        { word: 'play', americanPhonetic: '/pleɪ/', britishPhonetic: '/pleɪ/', meaning: '玩' },
      ]
    },
    {
      id: 'oa-team',
      pattern: 'oa',
      description: 'oa 组合发 /oʊ/ (美) 或 /əʊ/ (英) 音',
      category: 'long-vowel',
      americanSound: '/oʊ/',
      britishSound: '/əʊ/',
      examples: [
        { word: 'boat', americanPhonetic: '/boʊt/', britishPhonetic: '/bəʊt/', meaning: '船' },
        { word: 'coat', americanPhonetic: '/koʊt/', britishPhonetic: '/kəʊt/', meaning: '外套' },
        { word: 'road', americanPhonetic: '/roʊd/', britishPhonetic: '/rəʊd/', meaning: '路' },
        { word: 'goat', americanPhonetic: '/goʊt/', britishPhonetic: '/ɡəʊt/', meaning: '山羊' },
        { word: 'soap', americanPhonetic: '/soʊp/', britishPhonetic: '/səʊp/', meaning: '肥皂' },
      ]
    },
    {
      id: 'oo-team',
      pattern: 'oo',
      description: 'oo 组合发长音 /u/ 或短音 /ʊ/',
      category: 'long-vowel',
      americanSound: '/u/',
      britishSound: '/u/',
      examples: [
        { word: 'moon', americanPhonetic: '/mun/', britishPhonetic: '/muːn/', meaning: '月亮 (长音)' },
        { word: 'book', americanPhonetic: '/bʊk/', britishPhonetic: '/bʊk/', meaning: '书 (短音)' },
        { word: 'food', americanPhonetic: '/fud/', britishPhonetic: '/fuːd/', meaning: '食物 (长音)' },
        { word: 'good', americanPhonetic: '/ɡʊd/', britishPhonetic: '/ɡʊd/', meaning: '好 (短音)' },
        { word: 'cool', americanPhonetic: '/kul/', britishPhonetic: '/kuːl/', meaning: '凉爽 (长音)' },
      ]
    },
    {
      id: 'igh-team',
      pattern: 'igh',
      description: 'igh 组合发 /aɪ/ 音，gh 不发音',
      category: 'long-vowel',
      americanSound: '/aɪ/',
      britishSound: '/aɪ/',
      examples: [
        { word: 'high', americanPhonetic: '/haɪ/', britishPhonetic: '/haɪ/', meaning: '高' },
        { word: 'night', americanPhonetic: '/naɪt/', britishPhonetic: '/naɪt/', meaning: '夜晚' },
        { word: 'light', americanPhonetic: '/laɪt/', britishPhonetic: '/laɪt/', meaning: '光' },
        { word: 'right', americanPhonetic: '/raɪt/', britishPhonetic: '/raɪt/', meaning: '右边/正确' },
        { word: 'sight', americanPhonetic: '/saɪt/', britishPhonetic: '/saɪt/', meaning: '视力' },
      ]
    },
  ]
};

/**
 * Consonant Blends (辅音连缀)
 */
const blends: PhonicsCategory = {
  id: 'blends',
  name: '辅音连缀',
  icon: '🎹',
  description: '两个或多个辅音快速连续发音',
  rules: [
    {
      id: 'l-blends',
      pattern: 'bl, cl, fl, gl, pl, sl',
      description: 'l 连缀：辅音 + l 快速连接',
      category: 'blend',
      americanSound: 'bl-/cl-/fl-/gl-/pl-/sl-',
      examples: [
        { word: 'blue', americanPhonetic: '/blu/', britishPhonetic: '/blu/', meaning: '蓝色' },
        { word: 'clock', americanPhonetic: '/klɑk/', britishPhonetic: '/klɒk/', meaning: '钟' },
        { word: 'flower', americanPhonetic: '/ˈflaʊər/', britishPhonetic: '/ˈflaʊə/', meaning: '花' },
        { word: 'glad', americanPhonetic: '/glæd/', britishPhonetic: '/glæd/', meaning: '高兴' },
        { word: 'play', americanPhonetic: '/pleɪ/', britishPhonetic: '/pleɪ/', meaning: '玩' },
      ]
    },
    {
      id: 'r-blends',
      pattern: 'br, cr, dr, fr, gr, pr, tr',
      description: 'r 连缀：辅音 + r 快速连接',
      category: 'blend',
      americanSound: 'br-/cr-/dr-/fr-/gr-/pr-/tr-',
      examples: [
        { word: 'bread', americanPhonetic: '/bred/', britishPhonetic: '/bred/', meaning: '面包' },
        { word: 'cry', americanPhonetic: '/kraɪ/', britishPhonetic: '/kraɪ/', meaning: '哭' },
        { word: 'drink', americanPhonetic: '/drɪŋk/', britishPhonetic: '/drɪŋk/', meaning: '喝' },
        { word: 'frog', americanPhonetic: '/frɑɡ/', britishPhonetic: '/frɒɡ/', meaning: '青蛙' },
        { word: 'tree', americanPhonetic: '/tri/', britishPhonetic: '/tri/', meaning: '树' },
      ]
    },
    {
      id: 's-blends',
      pattern: 'sc, sk, sm, sn, sp, st, sw',
      description: 's 连缀：s + 辅音快速连接',
      category: 'blend',
      americanSound: 'sc-/sk-/sm-/sn-/sp-/st-/sw-',
      examples: [
        { word: 'scan', americanPhonetic: '/skæn/', britishPhonetic: '/skæn/', meaning: '扫描' },
        { word: 'sky', americanPhonetic: '/skaɪ/', britishPhonetic: '/skaɪ/', meaning: '天空' },
        { word: 'small', americanPhonetic: '/smɑl/', britishPhonetic: '/smɔːl/', meaning: '小' },
        { word: 'snake', americanPhonetic: '/sneɪk/', britishPhonetic: '/sneɪk/', meaning: '蛇' },
        { word: 'stop', americanPhonetic: '/stɑp/', britishPhonetic: '/stɒp/', meaning: '停止' },
      ]
    },
    {
      id: 'ending-blends',
      pattern: 'nd, nt, st, ld, mp, nk',
      description: '词尾连缀：辅音快速连接',
      category: 'blend',
      americanSound: '-nd/-nt/-st/-ld/-mp/-nk',
      examples: [
        { word: 'hand', americanPhonetic: '/hænd/', britishPhonetic: '/hænd/', meaning: '手' },
        { word: 'ant', americanPhonetic: '/ænt/', britishPhonetic: '/ænt/', meaning: '蚂蚁' },
        { word: 'fast', americanPhonetic: '/fæst/', britishPhonetic: '/fɑːst/', meaning: '快' },
        { word: 'cold', americanPhonetic: '/koʊld/', britishPhonetic: '/kəʊld/', meaning: '冷' },
        { word: 'bank', americanPhonetic: '/bæŋk/', britishPhonetic: '/bæŋk/', meaning: '银行' },
      ]
    },
  ]
};

/**
 * Special Rules (特殊规则)
 */
const specialRules: PhonicsCategory = {
  id: 'special',
  name: '特殊规则',
  icon: '⭐',
  description: '常见特殊发音规则',
  rules: [
    {
      id: 'silent-e',
      pattern: 'e (结尾)',
      description: '词尾 e 通常不发音，使前面的元音发长音',
      category: 'special',
      americanSound: 'silent',
      examples: [
        { word: 'cake', americanPhonetic: '/keɪk/', britishPhonetic: '/keɪk/', meaning: '蛋糕 - e 不发音' },
        { word: 'bike', americanPhonetic: '/baɪk/', britishPhonetic: '/baɪk/', meaning: '自行车 - e 不发音' },
        { word: 'rose', americanPhonetic: '/roʊz/', britishPhonetic: '/rəʊz/', meaning: '玫瑰 - e 不发音' },
        { word: 'cute', americanPhonetic: '/kuːt/', britishPhonetic: '/kuːt/', meaning: '可爱 - e 不发音' },
      ]
    },
    {
      id: 'silent-k',
      pattern: 'kn (开头)',
      description: 'kn 开头的词，k 不发音',
      category: 'special',
      americanSound: 'n (k silent)',
      examples: [
        { word: 'know', americanPhonetic: '/noʊ/', britishPhonetic: '/nəʊ/', meaning: '知道' },
        { word: 'knee', americanPhonetic: '/ni/', britishPhonetic: '/ni/', meaning: '膝盖' },
        { word: 'knife', americanPhonetic: '/naɪf/', britishPhonetic: '/naɪf/', meaning: '刀' },
        { word: 'knock', americanPhonetic: '/nɑk/', britishPhonetic: '/nɒk/', meaning: '敲' },
      ]
    },
    {
      id: 'silent-w',
      pattern: 'wr (开头)',
      description: 'wr 开头的词，w 不发音',
      category: 'special',
      americanSound: 'r (w silent)',
      examples: [
        { word: 'write', americanPhonetic: '/raɪt/', britishPhonetic: '/raɪt/', meaning: '写' },
        { word: 'wrong', americanPhonetic: '/rɑŋ/', britishPhonetic: '/rɒŋ/', meaning: '错误' },
        { word: 'wrap', americanPhonetic: '/ræp/', britishPhonetic: '/ræp/', meaning: '包裹' },
      ]
    },
    {
      id: 'soft-c',
      pattern: 'c (e, i, y 前)',
      description: 'c 在 e, i, y 前发 /s/，其他情况发 /k/',
      category: 'special',
      americanSound: '/s/',
      examples: [
        { word: 'city', americanPhonetic: '/ˈsɪti/', britishPhonetic: '/ˈsɪti/', meaning: '城市 (c 发 /s/)' },
        { word: 'cent', americanPhonetic: '/sent/', britishPhonetic: '/sent/', meaning: '分 (c 发 /s/)' },
        { word: 'cat', americanPhonetic: '/kæt/', britishPhonetic: '/kæt/', meaning: '猫 (c 发 /k/)' },
        { word: 'cup', americanPhonetic: '/kʌp/', britishPhonetic: '/kʌp/', meaning: '杯子 (c 发 /k/)' },
      ]
    },
    {
      id: 'soft-g',
      pattern: 'g (e, i, y 前)',
      description: 'g 在 e, i, y 前通常发 /dʒ/，其他情况发 /g/',
      category: 'special',
      americanSound: '/dʒ/',
      examples: [
        { word: 'gym', americanPhonetic: '/dʒɪm/', britishPhonetic: '/dʒɪm/', meaning: '健身房 (g 发 /dʒ/)' },
        { word: 'giant', americanPhonetic: '/ˈdʒaɪənt/', britishPhonetic: '/ˈdʒaɪənt/', meaning: '巨人 (g 发 /dʒ/)' },
        { word: 'go', americanPhonetic: '/goʊ/', britishPhonetic: '/gəʊ/', meaning: '去 (g 发 /g/)' },
        { word: 'game', americanPhonetic: '/geɪm/', britishPhonetic: '/geɪm/', meaning: '游戏 (g 发 /g/)' },
      ]
    },
    {
      id: 'r-controlled',
      pattern: 'ar, or, ir, er, ur',
      description: 'r 控制元音，发音改变',
      category: 'special',
      americanSound: 'various',
      examples: [
        { word: 'car', americanPhonetic: '/kɑr/', britishPhonetic: '/kɑː/', meaning: '汽车 - ar 发 /ɑr/' },
        { word: 'for', americanPhonetic: '/fɔr/', britishPhonetic: '/fɔː/', meaning: '为了 - or 发 /ɔr/' },
        { word: 'bird', americanPhonetic: '/bɜrd/', britishPhonetic: '/bɜːd/', meaning: '鸟 - ir 发 /ɜr/' },
        { word: 'her', americanPhonetic: '/hɜr/', britishPhonetic: '/hɜː/', meaning: '她 - er 发 /ɜr/' },
        { word: 'turn', americanPhonetic: '/tɜrn/', britishPhonetic: '/tɜːn/', meaning: '转 - ur 发 /ɜr/' },
      ]
    },
  ]
};

/**
 * All phonics categories
 */
export const phonicsCategories: PhonicsCategory[] = [
  shortVowels,
  longVowels,
  vowelTeams,
  digraphs,
  blends,
  specialRules,
];

/**
 * Get all phonics rules as flat array
 */
export function getAllPhonicsRules(): PhonicsRule[] {
  return phonicsCategories.flatMap(cat => cat.rules);
}

/**
 * Get phonics category by ID
 */
export function getPhonicsCategory(id: string): PhonicsCategory | undefined {
  return phonicsCategories.find(cat => cat.id === id);
}

/**
 * Get phonics rule by ID
 */
export function getPhonicsRule(id: string): PhonicsRule | undefined {
  return getAllPhonicsRules().find(rule => rule.id === id);
}

/**
 * Get examples for a specific pronunciation style
 */
export function getExamplesForStyle(
  rule: PhonicsRule,
  style: 'american' | 'british'
): PhonicsExample[] {
  return rule.examples.map(ex => ({
    ...ex,
    phonetic: style === 'american' ? ex.americanPhonetic : ex.britishPhonetic
  }));
}